import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  ReactNode,
} from "react";
import { createRealtimeTranscription } from "../services/realtimeTranscription";

interface TranscriptionError {
  message: string;
  type: string;
}

interface RealtimeTranscriptionContextType {
  isConnected: boolean;
  isRecording: boolean;
  transcript: string;
  interimTranscripts: string[];
  audioLevel: number;
  completeTranscript: string;
  accumulatedTranscript: string;
  error: TranscriptionError | null;
  startAgain: () => Promise<void>;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  resetTranscription: () => void;
  getAudioLevel: () => number;
  setAudioLevel: (level: number) => void;
}

const RealtimeTranscriptionContext =
  createContext<RealtimeTranscriptionContextType | null>(null);

export const RealtimeTranscriptionProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState("");
  const [interimTranscripts, setInterimTranscripts] = useState<string[]>([]);
  const [completeTranscript, setCompleteTranscript] = useState("");
  const [error, setError] = useState<TranscriptionError | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const realtimeTranscriptionRef = useRef<ReturnType<
    typeof createRealtimeTranscription
  > | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    const initConnection = async () => {
      try {
        realtimeTranscriptionRef.current = createRealtimeTranscription(
          (text, isFinal) => handleTranscription(text, isFinal),
          (error) => {
            console.error("Transcription error:", error);
            setError({
              message: getErrorMessage(error.message),
              type: error.type || "transcription_error",
            });
          },
          {
            language: "en",
            onRtcConnected: () => {
              console.log("RTC connected");
              setIsConnected(true);
            },
          }
        );

        await realtimeTranscriptionRef.current.start();
        realtimeTranscriptionRef.current.disableStreaming();
      } catch (err: any) {
        console.error("Failed to initialize transcription:", err);
        setError({
          message: getErrorMessage(err.message),
          type: "connection_error",
        });
      }
    };

    initConnection();

    return () => {
      isMounted.current = false;
      if (realtimeTranscriptionRef.current) {
        realtimeTranscriptionRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    console.log("Setting transcript", currentTranscript);
    console.log(
      "Setting completeTranscript",
      `${completeTranscript} ${currentTranscript}`
    );
    setCompleteTranscript(`${completeTranscript} ${currentTranscript}`);
  }, [currentTranscript]);

  const handleTranscription = useCallback(
    (text: string, isFinal: boolean) => {
      if (isFinal) {
        setCompleteTranscript((prev) =>
          [prev, ...interimTranscripts, text].filter(Boolean).join(" ").trim()
        );
        setInterimTranscripts([]);
        setCurrentTranscript("");
      } else {
        setCurrentTranscript(text);
      }
    },
    [interimTranscripts]
  );

  const startRecording = useCallback(async () => {
    if (!realtimeTranscriptionRef.current) {
      throw new Error("Transcription service not initialized");
    }

    setError(null);
    resetTranscription();

    try {
      await realtimeTranscriptionRef.current.startListening();
      realtimeTranscriptionRef.current.enableStreaming();
      setIsRecording(true);
    } catch (err) {
      console.error("Failed to start recording:", err);
      setError({
        message: getErrorMessage(err),
        type: "permission_error",
      });
      realtimeTranscriptionRef.current.stopListening();
      throw err;
    }
  }, []);

  const stopRecording = useCallback(async () => {
    if (realtimeTranscriptionRef.current && isRecording) {
      if (currentTranscript) {
        setCompleteTranscript((prev) =>
          [prev, ...interimTranscripts].filter(Boolean).join(" ").trim()
        );
        setInterimTranscripts([]);
        setCurrentTranscript("");
      }

      await new Promise((resolve) => setTimeout(resolve, 500));
      realtimeTranscriptionRef.current.stopListening();
      realtimeTranscriptionRef.current.disableStreaming();
      setIsRecording(false);
    }
  }, [isRecording, currentTranscript, interimTranscripts]);

  const startAgain = useCallback(async () => {
    if (!realtimeTranscriptionRef.current) {
      throw new Error("Transcription service not initialized");
    }
    await realtimeTranscriptionRef.current.start();
    realtimeTranscriptionRef.current.disableStreaming();
  }, []);

  const resetTranscription = useCallback(() => {
    setCurrentTranscript("");
    setInterimTranscripts([]);
    setCompleteTranscript("");
    setError(null);
  }, []);

  const getAudioLevel = useCallback(() => audioLevel, [audioLevel]);

  const accumulatedTranscript = useMemo(() => {
    const parts = [
      completeTranscript,
      ...interimTranscripts,
      ...(currentTranscript ? [currentTranscript] : []),
    ].filter(Boolean);
    return parts.join(" ").replace(/\s+/g, " ").trim();
  }, [completeTranscript, interimTranscripts, currentTranscript]);

  const value = useMemo(
    () => ({
      isConnected,
      isRecording,
      transcript: currentTranscript,
      interimTranscripts,
      audioLevel,
      completeTranscript,
      accumulatedTranscript,
      error,
      startAgain,
      startRecording,
      stopRecording,
      resetTranscription,
      getAudioLevel,
      setAudioLevel,
    }),
    [
      isConnected,
      isRecording,
      currentTranscript,
      interimTranscripts,
      audioLevel,
      completeTranscript,
      accumulatedTranscript,
      error,
      startAgain,
      startRecording,
      stopRecording,
      resetTranscription,
      getAudioLevel,
    ]
  );

  return (
    <RealtimeTranscriptionContext.Provider value={value}>
      {children}
    </RealtimeTranscriptionContext.Provider>
  );
};

export const useRealtimeTranscriptionContext = () => {
  const context = useContext(RealtimeTranscriptionContext);
  if (!context) {
    throw new Error(
      "useRealtimeTranscriptionContext must be used within a RealtimeTranscriptionProvider"
    );
  }
  return context;
};

function getErrorMessage(error: unknown): string {
  if (!error) return "An unknown error occurred";

  if (error instanceof DOMException) {
    if (
      error.name === "NotAllowedError" ||
      error.name === "PermissionDeniedError"
    ) {
      return "Microphone access was denied. Please allow microphone access.";
    }
    if (error.name === "NotSupportedError") {
      return "Your browser doesn't support this feature.";
    }
    return error.message || "A browser error occurred";
  }

  if (error instanceof Error) {
    if (
      error.message.includes("Network") ||
      error.message.includes("Failed to fetch")
    ) {
      return "Network error. Please check your internet connection.";
    }
    return error.message || "An error occurred";
  }

  if (typeof error === "string") return error;

  return "An unknown error occurred";
}
